import React, { useState } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import CustomButton from "../common/CustomButton";
import CustomTextField from "../common/CustomTextField";
import CustomAutocomplete from '../common/CustomAutoComplete';
import CustomTextarea from "../common/CustomTextarea";
import { genericPost } from '../../services/authServices';
import { CREATE_EXPORT_SHIPMENT } from '../../services/apiRoutes';
import CustomStyle from "../common/CustomStyle";
import { t } from "../../constants/translationHelper";

const shipmentValidationSchema = (newShippingId, expShipmentId) =>
    Yup.object().shape({
        shipmentId: Yup.string()
            .required('Shipment ID is required')
            .min(2, 'Shipment ID must be at least 2 characters long')
            .test(
                'unique-shipment-id',
                'Your Shipment ID matches an already generated ID.',
                function (value) {
                    return expShipmentId === 1 ? value !== newShippingId : true;
                }
            ),
        customerId: Yup.number().required('Customer is required'),
        cifFobId: Yup.number().required('CIF/FOB is required'),
        noOfContainers: Yup.number().required('Number of Containers is required'),
        currencyId: Yup.number().required('Currency is required'),
        shipmentTypeId: Yup.number().required('Shipment Type  is required'),
        paymentMethodId: Yup.number().required('Payment Method is required'),
        expectedEtd: Yup.date()
            .typeError('Expected ETD must be a valid date')
            .required('Expected ETD is required'),

        shipmentDate: Yup.date()
            .typeError('Shipment Date must be a valid date')
            .required('Shipment Date is required'),


    });
const Createexportshipement = ({ handleClose, setMessage, portMasters, exportShipments, fetchExportShipments, cifFobList, shipmentTypes, currencyList, custumersList, paymentMethod, newShippingId, expShipmentId }) => {

    const [shipmentError, setShipmentError] = useState("");

    const handleSubmit = async (values, { setSubmitting }) => {
        if (shipmentError) {
            setSubmitting(false);
            return;
        }
        try {
            const response = await genericPost(CREATE_EXPORT_SHIPMENT, values);

            if (response.status === 201) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Shipment added successfully",
                    type: "success",
                });
                setTimeout(() => {
                    fetchExportShipments();
                    handleClose();
                },);
            } else if (response.status === 409) {
                setMessage({
                    open: true,
                    text: response.data?.message || "Export Shipment already exists.",
                    type: 'error',
                });
            }
            else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    const [formData] = useState({
        expShipmentId: expShipmentId,
        // shipmentId: expShipmentId === 1 ? "" : newShippingId,
        shipmentId: expShipmentId === 1 ? "" : newShippingId,
        customerId: '',
        cifFobId: '',
        expectedEtd: '',
        shipmentDate: '',
        noOfContainers: '',
        currencyId: '',
        shipmentTypeId: '',
        paymentMethodId: '',
        portOfLoading: '',
        portOfDestination: '',
        note: '',
    });
    const handleShipmentIdChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue("shipmentId", value);

        if (expShipmentId === 1 && value === newShippingId) {
            setShipmentError("Your Shipment ID matches an already generated ID.");
        } else {
            setShipmentError("");
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={formData}
                validationSchema={shipmentValidationSchema(newShippingId)}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Create Export Shipment")}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting || !!shipmentError}
                                    />
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title=" Save and New"
                                        size="small"
                                        variant="contained"
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={custumersList}
                                    fieldLabel="Customer"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={custumersList.find((option) => option.id === values.customerId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('customerId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Customer"
                                    error={touched.customerId && !!errors.customerId}
                                    helperText={touched.customerId && errors.customerId}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    type="text"
                                    placeholder="Shipment ID"
                                    name="shipmentId"
                                    fieldLabel="Shipment ID"
                                    required={true}
                                    value={values.shipmentId}
                                    onChange={(e) => handleShipmentIdChange(e, setFieldValue)}
                                    error={!!shipmentError || (touched.shipmentId && !!errors.shipmentId)}
                                    helperText={shipmentError || (touched.shipmentId && errors.shipmentId)}
                                    readOnly={expShipmentId !== 1}
                                    sx={expShipmentId !== 1 ? CustomStyle.readOnly : {}}
                                />

                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    name="shipmentDate"
                                    fieldLabel="Date"
                                    type="date"
                                    required={true}
                                    value={values.shipmentDate}
                                    onChange={handleChange}
                                    error={touched.shipmentDate && !!errors.shipmentDate}
                                    helperText={touched.shipmentDate && errors.shipmentDate}
                                />
                            </Grid>



                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={cifFobList}
                                    fieldLabel="CIF/FOB"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={cifFobList.find((option) => option.id === values.cifFobId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('cifFobId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select CIF/FOB"
                                    error={touched.cifFobId && !!errors.cifFobId}
                                    helperText={touched.cifFobId && errors.cifFobId}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    name="expectedEtd"
                                    fieldLabel="Expected ETD"
                                    required={true}
                                    type="date"
                                    value={values.expectedEtd}
                                    onChange={handleChange}
                                    error={touched.expectedEtd && !!errors.expectedEtd}
                                    helperText={touched.expectedEtd && errors.expectedEtd}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    type="number"
                                    name="noOfContainers"
                                    fieldLabel="Number of Containers"
                                    placeholder="Enter Number of Containers"
                                    required={true}
                                    value={values.noOfContainers}
                                    onChange={handleChange}
                                    error={touched.noOfContainers && !!errors.noOfContainers}
                                    helperText={touched.noOfContainers && errors.noOfContainers}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.currencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currencyId && !!errors.currencyId}
                                    helperText={touched.currencyId && errors.currencyId}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={shipmentTypes}
                                    fieldLabel="Shipment Type "
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={shipmentTypes.find((option) => option.id === values.shipmentTypeId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('shipmentTypeId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Shipment Type "
                                    error={touched.shipmentTypeId && !!errors.shipmentTypeId}
                                    helperText={touched.shipmentTypeId && errors.shipmentTypeId}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={paymentMethod}
                                    fieldLabel="Payment Method"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={paymentMethod.find((option) => option.id === values.paymentMethodId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('paymentMethodId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Payment Method "
                                    error={touched.paymentMethodId && !!errors.paymentMethodId}
                                    helperText={touched.paymentMethodId && errors.paymentMethodId}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={portMasters}
                                    fieldLabel="Port Of Loading"
                                    getOptionLabel={(option) => option.title}
                                    value={portMasters.find((option) => option.id === values.portOfLoading) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('portOfLoading', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Port Of Loading"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={portMasters}
                                    fieldLabel="Port Of Destination"
                                    getOptionLabel={(option) => option.title}
                                    value={portMasters.find((option) => option.id === values.portOfDestination) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('portOfDestination', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Port Of Destination"
                                />
                            </Grid>

                            <Grid item xs={8}>
                                <CustomTextarea
                                    placeholder="Write a Notes .. "
                                    name="note"
                                    fieldLabel="Note"
                                    value={values.note}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default Createexportshipement;

